'use client';

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Globe, Sparkles, Check, CheckCircle2, 
  HelpCircle, Server, RefreshCw, ChevronRight, Lock 
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { toast } from 'sonner'

export default function CustomDomainLinksClient() {
  const [domainInput, setDomainInput] = useState('links.yourbrand.com')
  const [step, setStep] = useState<'input' | 'dns' | 'verifying' | 'success'>('input')
  const [verifyStatus, setVerifyStatus] = useState('')

  const handleNextToDns = (e: React.FormEvent) => {
    e.preventDefault()
    if (!domainInput || !domainInput.includes('.')) {
      toast.error('Please enter a valid domain (e.g. links.mybrand.com)')
      return
    }
    setStep('dns')
    toast.success('Domain settings configured. Proceed to DNS setup.')
  }

  const handleVerifyDns = () => {
    setStep('verifying')
    setVerifyStatus('Querying DNS servers...')

    setTimeout(() => {
      setVerifyStatus('Verifying CNAME mapping...')
      
      setTimeout(() => {
        setVerifyStatus('Issuing SSL certificate (Let\'s Encrypt)...')
        
        setTimeout(() => {
          setStep('success')
          toast.success('Custom domain SSL verified successfully!')
        }, 1200)
      }, 1000)
    }, 1000)
  }

  const resetDnsWizard = () => {
    setStep('input')
    setDomainInput('links.yourbrand.com')
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
        <motion.div 
          className="text-center space-y-6 max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
            <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              Custom Domain Manager
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
            Custom Domain Links<br />
            <span className="text-gray-500">Short URLs with own brand</span><br />
            CNAME redirects
          </motion.h1>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
            Boost click-through rates by up to 34% by wrapping link redirects in your own custom domain. Rebuild user trust with branded URLs.
          </motion.p>
        </motion.div>

        {/* DNS Configuration Wizard Sandbox */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="w-full max-w-3xl mt-12 bg-background/40 border border-white/10 rounded-3xl p-6 md:p-8 backdrop-blur-xl shadow-[0_0_80px_rgba(0,102,255,0.05)]"
        >
          <div className="flex justify-between items-center border-b border-white/5 pb-4 mb-6">
            <div>
              <h2 className="text-sm font-mono tracking-widest text-primary/80 uppercase flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Live Domain Setup Wizard
              </h2>
              <p className="text-[10px] text-white/40 font-mono">Simulate a DNS record verification step</p>
            </div>
            
            {/* Step markers */}
            <div className="flex items-center gap-1.5 text-xs text-white/30 font-mono hidden sm:flex">
              <span className={step === 'input' ? 'text-primary font-bold' : ''}>1. Domain</span>
              <ChevronRight className="w-3 h-3" />
              <span className={step === 'dns' ? 'text-primary font-bold' : ''}>2. DNS</span>
              <ChevronRight className="w-3 h-3" />
              <span className={step === 'verifying' ? 'text-primary font-bold' : ''}>3. Verify</span>
              <ChevronRight className="w-3 h-3" />
              <span className={step === 'success' ? 'text-emerald-400 font-bold' : ''}>4. Ready</span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 'input' && (
              <motion.form 
                key="step-input"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={handleNextToDns}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-sm font-semibold text-white uppercase tracking-tight">Step 1: Enter your domain</h3>
                  <p className="text-xs text-white/50 mt-1">
                    Input the custom domain or subdomain you wish to use for link redirects.
                  </p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="domain-input-field" className="text-[10px] font-mono text-white/40 uppercase">Domain name</label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      id="domain-input-field"
                      type="text"
                      placeholder="links.yourbrand.com"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-2xl text-sm focus:outline-none focus:border-primary/60 transition-all font-light"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 bg-primary text-white font-bold rounded-2xl text-sm transition-all hover:bg-primary/95 shadow-[0_0_20px_rgba(0,102,255,0.3)] active:scale-95"
                >
                  Configure DNS Records
                </button>
              </motion.form>
            )}

            {step === 'dns' && (
              <motion.div 
                key="step-dns"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-sm font-semibold text-white uppercase tracking-tight">Step 2: Add DNS records</h3>
                  <p className="text-xs text-white/50 mt-1">
                    Log in to your domain registrar (GoDaddy, Cloudflare, etc.) and add the following CNAME record.
                  </p>
                </div>

                {/* DNS record table */}
                <div className="p-4 bg-white/[0.01] border border-white/5 rounded-2xl space-y-3">
                  <div className="grid grid-cols-3 text-[10px] font-mono text-white/40 uppercase border-b border-white/5 pb-2">
                    <span>Type</span>
                    <span>Host / Name</span>
                    <span>Points To / Value</span>
                  </div>
                  
                  <div className="grid grid-cols-3 text-xs font-mono text-white py-1">
                    <span className="text-primary font-bold">CNAME</span>
                    <span>{domainInput.split('.')[0] || 'links'}</span>
                    <span className="text-emerald-400 font-bold select-all">cname.lenklyst.com</span>
                  </div>
                </div>

                <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl text-[10px] text-white/60 font-mono flex gap-2">
                  <HelpCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  Note: DNS propagation can take anywhere from a few seconds up to 24 hours depending on your registrar.
                </div>

                <button
                  onClick={handleVerifyDns}
                  className="w-full py-4 bg-primary text-white font-bold rounded-2xl text-sm transition-all hover:bg-primary/95 shadow-[0_0_20px_rgba(0,102,255,0.3)] active:scale-95"
                >
                  Verify DNS Propagation
                </button>
              </motion.div>
            )}

            {step === 'verifying' && (
              <motion.div 
                key="step-verifying"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 flex flex-col items-center justify-center text-center space-y-4"
              >
                <div className="relative w-12 h-12">
                  <div className="absolute inset-0 border-4 border-white/10 rounded-full" />
                  <div className="absolute inset-0 border-4 border-t-primary rounded-full animate-spin" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-white">{verifyStatus}</p>
                  <p className="text-[10px] text-white/40 font-mono">Running live NS query check</p>
                </div>
              </motion.div>
            )}

            {step === 'success' && (
              <motion.div 
                key="step-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="py-6 flex flex-col items-center justify-center text-center space-y-6"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-xl font-bold uppercase tracking-tight text-white">Domain Connected!</h4>
                  <p className="text-xs text-white/50 max-w-[320px]">
                    Your domain <span className="font-semibold text-white">{domainInput}</span> is fully active. Redirection and SSL routing are fully simulated.
                  </p>
                </div>

                <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl w-full text-left space-y-2 max-w-sm">
                  <div className="flex justify-between text-[10px] font-mono">
                    <span className="text-white/40 uppercase">Domain</span>
                    <span className="text-white">{domainInput}</span>
                  </div>
                  <div className="flex justify-between text-[10px] font-mono">
                    <span className="text-white/40 uppercase">SSL certificate</span>
                    <span className="text-emerald-400 flex items-center gap-1"><Lock className="w-3 h-3" /> Active (Let&apos;s Encrypt)</span>
                  </div>
                  <div className="flex justify-between text-[10px] font-mono">
                    <span className="text-white/40 uppercase">Redirection</span>
                    <span className="text-white">Active</span>
                  </div>
                </div>

                <button
                  onClick={resetDnsWizard}
                  className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-semibold rounded-xl transition-all"
                >
                  Configure Another Domain
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-20 border-t border-white/5 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Server className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Automatic SSL Provision</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              We auto-renew Let&apos;s Encrypt wildcard SSL certificates for every custom domain connected. Ensure secure, HTTPS-wrapped redirects always.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Root Domain Redirects</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Define fallback URLs. If a customer visits your raw subdomain (e.g. `links.brand.com`) without a path, redirect them to your main corporate site.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Anycast DNS Network</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Our URL redirection servers are distributed across a multi-region global Cloudflare network, reducing DNS resolution latency to under 30ms.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Brand your routing links</h2>
        <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Boost user click confidence. Configure custom domain shortcuts for smart links in minutes.</p>
        <Link 
          href="/auth/sign-up" 
          className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
        >
          Connect Custom Domain <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <MarketingFooter />
    </div>
  )
}
